import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { accountsApi } from '../../api/accounts'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import type { ExpansionView } from '../../types/accounts'
import type { Opening, OpeningReason } from '../../types/valueProps'
import './expand.css'

// Land and expand (PM-007): where the firm is mid-delivery — peak expectation — and could sell the next
// service line. Every "could sell next" tag is the sum of reasons the person can open (the thread that
// mentions it, the decision maker on file, the tactic that applies), and pressing it opens the value
// proposition workspace for that line at that account.

const REASON_WORDS: Record<string, string> = {
  'peak-expectation': 'Peak expectation', 'adjacent-line': 'Adjacent line', 'mentioned-in-mail': 'In their mail', 'amount-on-table': 'Money on the table',
  'decision-maker-reached': 'Decision maker', 'champion-present': 'Champion', 'influencer-present': 'Influencer', 'tactic-due': 'Tactic due', 'no-route': 'No route yet',
}

export function reasonWord(kind: string) { return REASON_WORDS[kind] ?? kind }

function openingsOf(x: ExpansionView): Opening[] {
  return x.openings && x.openings.length > 0 ? x.openings : x.suggested.map((line) => ({ line, score: 0, reasons: [] }))
}

export function Expand() {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['expand', spoke], queryFn: () => accountsApi.expand(spoke), staleTime: 20_000 })
  const rows = q.data?.expansions ?? []
  const names = q.data?.ownerNames ?? {}
  const scope = scopeWords(q.data?.scope)
  const glance = useMemo(() => {
    const openings = rows.flatMap(openingsOf)
    const has = (kind: string) => rows.filter((x) => openingsOf(x).some((o) => o.reasons.some((r) => r.kind === kind))).length
    return {
      accounts: rows.length,
      openings: openings.length,
      inMail: openings.filter((o) => o.reasons.some((r) => r.kind === 'mentioned-in-mail')).length,
      withDecisionMaker: has('decision-maker-reached'),
      noRoute: has('no-route'),
      tacticDue: has('tactic-due'),
    }
  }, [rows])

  return (
    <section className="page expand">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{scope ? ` · ${scope}` : ''}</p>
          <h1>Land &amp; Expand</h1>
          <p className="lede">Accounts you are delivering right now — the moment of peak expectation — and the service lines you have not yet sold them. Every tag says why it shows; press one to write the value proposition.</p>
        </div>
      </header>

      {q.isSuccess && rows.length > 0 && (
        <div className="deal-totals">
          <div className="stat accent"><strong>{glance.accounts}</strong><span className="muted">accounts in delivery</span></div>
          <div className="stat"><strong>{glance.openings}</strong><span className="muted">openings</span></div>
          <div className="stat"><strong>{glance.inMail}</strong><span className="muted">asked for in their mail</span></div>
          <div className="stat"><strong>{glance.withDecisionMaker}</strong><span className="muted">with a decision maker on file</span></div>
          <div className="stat"><strong>{glance.noRoute}</strong><span className="muted">with no route yet</span></div>
          <div className="stat"><strong>{glance.tacticDue}</strong><span className="muted">with a tactic due</span></div>
        </div>
      )}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error.message}</p></div>}
      {q.isSuccess && rows.length === 0 && <div className="card"><p className="muted">No active delivery engagements on file. Once a pursuit is being delivered, its expansion openings show here.</p></div>}

      {rows.length > 0 && (
        <div className="expand-list">
          {rows.map((x) => <ExpansionCard key={x.company.companyId} x={x} names={names} />)}
        </div>
      )}
    </section>
  )
}

function ExpansionCard({ x, names }: { x: ExpansionView; names: Record<string, string> }) {
  const openings = openingsOf(x)
  const people = openings[0]?.reasons.filter((r) => ['decision-maker-reached', 'champion-present', 'influencer-present'].includes(r.kind)) ?? []
  const noRoute = openings[0]?.reasons.some((r) => r.kind === 'no-route') ?? false
  return (
    <div className="card expand-card">
      <div className="expand-head">
        <div>
          <Link to={`/accounts/${encodeURIComponent(x.company.companyId)}`} className="expand-name">{x.company.name}</Link>
          <span className="muted small">{[x.company.sector, x.ownerIds.map((o) => names[o] ?? o).join(', ')].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="expand-meta">
          <span className="muted small">{x.deliveryCount} in delivery{x.lastActivity ? ` · last ${formatDate(x.lastActivity)}` : ''}</span>
          {people.map((r) => <span key={r.kind} className={`tag persona ${r.kind}`} title={r.evidence ?? undefined}>{r.headline}</span>)}
          {noRoute && <span className="tag sig watch">no route to a decision maker yet</span>}
        </div>
      </div>
      <div className="expand-lines">
        <div>
          <p className="expand-label">Engaged</p>
          {x.engaged.length > 0 ? <ul className="chips">{x.engaged.map((l) => <li key={l} className="chip on">{l}</li>)}</ul> : <span className="muted small">the model did not name a service line</span>}
        </div>
        <div>
          <p className="expand-label">Could sell next</p>
          {openings.length > 0 ? (
            <ul className="openings">
              {openings.map((o) => <OpeningButton key={o.line} companyId={x.company.companyId} o={o} />)}
            </ul>
          ) : <span className="muted small">every roster line is already engaged</span>}
        </div>
      </div>
    </div>
  )
}

function OpeningButton({ companyId, o }: { companyId: string; o: Opening }) {
  const why = o.reasons.filter((r) => r.kind !== 'no-route' && r.kind !== 'peak-expectation')
  const lead: OpeningReason | undefined = why[0] ?? o.reasons.find((r) => r.kind === 'peak-expectation')
  const strength = o.score >= 8 ? 'hot' : o.score >= 4 ? 'warm' : 'faint'
  return (
    <li>
      <Link className={`opening ${strength}`} to={`/expand/${encodeURIComponent(companyId)}/${encodeURIComponent(o.line)}`} title={o.reasons.map((r) => `${reasonWord(r.kind)}: ${r.headline}${r.evidence ? ` — ${r.evidence}` : ''}`).join('\n')}>
        <span className="opening-line"><strong>{o.line}</strong><span className="opening-score" aria-label={`score ${o.score}`}>{o.score > 0 ? o.score : '·'}</span></span>
        <span className="opening-why">{lead ? lead.headline : 'in delivery — peak expectation'}</span>
        <span className="opening-kinds">
          {why.slice(0, 3).map((r) => <em key={r.kind + r.headline} className={`kind ${r.kind}`}>{reasonWord(r.kind)}</em>)}
          {why.length > 3 && <em className="kind more">+{why.length - 3}</em>}
        </span>
        <span className="opening-cta">Write the value proposition →</span>
      </Link>
    </li>
  )
}
